import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Message } from "../../lib/messagesLib";
import {
  createRule,
  RulePatternTarget,
  MessageAction,
} from "../../lib/rulesLib";
import Tooltip from "../Tooltip";

interface MessageRowProps {
  message: Message;
  onRuleCreated?: () => Promise<void>;
  showSnackbar?: (message: string, type: "success" | "error" | "info") => void;
  // onViewDetails?: (messageId: string) => void;
}

const formatCompactDate = (timestamp: string) => {
  const date = new Date(timestamp);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else if (now.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString([], { weekday: "short" });
  } else {
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  }
};

const formatFullDate = (timestamp: string) => {
  const date = new Date(timestamp);
  return date.toLocaleDateString([], {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const MessageRow: React.FC<MessageRowProps> = ({
  message,
  onRuleCreated,
  showSnackbar,
}) => {
  // State for pill menu
  const [isPillMenuOpen, setIsPillMenuOpen] = useState(false);
  const pillRef = useRef<HTMLSpanElement>(null);

  // Close menu on outside click or Escape
  useEffect(() => {
    if (!isPillMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      // Check if click is outside both the pill trigger and the popover
      const target = e.target as Node;
      const isClickOnPill = pillRef.current && pillRef.current.contains(target);

      // Check if click is on the popover (which is rendered via portal)
      const popoverElement = document.querySelector("[data-pill-menu-popover]");
      const isClickOnPopover =
        popoverElement && popoverElement.contains(target);

      if (!isClickOnPill && !isClickOnPopover) {
        setIsPillMenuOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsPillMenuOpen(false);
    };
    const handleScroll = () => {
      setIsPillMenuOpen(false);
    };
    const handleResize = () => {
      setIsPillMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isPillMenuOpen]);

  // Disposition message logic
  let dispositionMsg: React.ReactNode = "This message wasn't processed";
  const action = message.messageAction?.actionType;
  const mode = message.messageAction?.mode;
  const selectedRuleId = (message.messageAction as MessageAction)
    ?.selectedRuleId;
  const sender = message.from[0]?.address || "";
  if (mode === "applied" && action === "allow") {
    if (selectedRuleId) {
      dispositionMsg = (
        <span>
          Sender {sender} was allowed by your rule (
          <a
            href={`/rules?filter=${selectedRuleId}`}
            className="text-blue-600 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            view rule
          </a>
          )
        </span>
      );
    } else {
      dispositionMsg = `Sender ${sender} was allowed by Cobutler`;
    }
  } else if (
    mode === "applied" &&
    (action === "filter" || action === "blocked")
  ) {
    if (selectedRuleId) {
      dispositionMsg = (
        <span>
          Sender {sender} was blocked by your rule (
          <a
            href={`/rules/${selectedRuleId}`}
            className="text-blue-600 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            view rule
          </a>
          )
        </span>
      );
    } else {
      dispositionMsg = `Sender ${sender} was blocked by Cobutler`;
    }
  } else if (action === "no action" || !action) {
    dispositionMsg = `We didn't scan this message from ${sender} (learn more)`;
  }

  // Handler to create a rule from a message
  const createRuleFromMessage = async (
    messageId: string,
    action: "allow" | "filter",
    targetType: "sender" | "domain",
    targetValue: string
  ) => {
    try {
      const result = await createRule({
        messageId,
        scopeTargetType: "user",
        scopeTargetId: null,
        ruleAction: action,
        patterns: [
          {
            target:
              targetType === "sender"
                ? RulePatternTarget.SENDER_ADDRESS
                : RulePatternTarget.SENDER_DOMAIN,
            value: targetValue,
          },
        ],
      });

      // Show success snackbar
      if (showSnackbar) {
        const actionText = action === "allow" ? "allowed" : "blocked";
        const targetText = targetType === "sender" ? "address" : "domain";
        showSnackbar(
          `Rule created: ${targetText} ${targetValue} will be ${actionText}`,
          "success"
        );
      }

      // Refresh data
      if (onRuleCreated) {
        await onRuleCreated();
      }

      console.log("Rule creation result:", result);
    } catch (error) {
      console.error("Failed to create rule:", error);
      if (showSnackbar) {
        showSnackbar("Failed to create rule. Please try again.", "error");
      }
    }
  };

  // Action buttons logic
  let buttons: { label: string; onClick: () => void; color: string }[] = [];
  const domainValue = message.from[0]?.domain || "";
  if (mode === "applied" && action === "allow") {
    buttons = [
      {
        label: `Block address`,
        onClick: () =>
          createRuleFromMessage(message.id, "filter", "sender", sender),
        color: "bg-red-100 text-red-700",
      },
      {
        label: `Block domain`,
        onClick: () =>
          createRuleFromMessage(message.id, "filter", "domain", domainValue),
        color: "bg-red-100 text-red-700",
      },
    ];
  } else if (
    mode === "applied" &&
    (action === "filter" || action === "blocked" || action === "challenge")
  ) {
    buttons = [
      {
        label: `Allow address`,
        onClick: () =>
          createRuleFromMessage(message.id, "allow", "sender", sender),
        color: "bg-green-100 text-green-700",
      },
      {
        label: `Allow domain`,
        onClick: () =>
          createRuleFromMessage(message.id, "allow", "domain", domainValue),
        color: "bg-green-100 text-green-700",
      },
    ];
  } else {
    buttons = [
      {
        label: `Allow address`,
        onClick: () =>
          createRuleFromMessage(message.id, "allow", "sender", sender),
        color: "bg-green-100 text-green-700",
      },
      {
        label: `Allow domain`,
        onClick: () =>
          createRuleFromMessage(message.id, "allow", "domain", domainValue),
        color: "bg-green-100 text-green-700",
      },
      {
        label: `Block address`,
        onClick: () =>
          createRuleFromMessage(message.id, "filter", "sender", sender),
        color: "bg-red-100 text-red-700",
      },
      {
        label: `Block domain`,
        onClick: () =>
          createRuleFromMessage(message.id, "filter", "domain", domainValue),
        color: "bg-red-100 text-red-700",
      },
    ];
  }

  return (
    <div className="flex items-center p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded">
      {/* Date */}
      <div className="w-20 flex-shrink-0 text-center">
        <Tooltip
          text={formatFullDate(message.timestamp)}
          className="whitespace-nowrap"
        >
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {formatCompactDate(message.timestamp)}
          </span>
        </Tooltip>
      </div>

      {/* Sender + Subject */}
      <div className="flex flex-col flex-1 min-w-0 px-2">
        <Tooltip
          text={
            <div className="text-center">
              <div className="font-medium">
                {message.from[0]?.name || (
                  <span className="italic text-gray-300">no name</span>
                )}
              </div>
              <div className="text-gray-300">
                {message.from[0]?.address || (
                  <span className="italic">no address</span>
                )}
              </div>
            </div>
          }
          className="max-w-md"
        >
          <p className="text-sm font-medium truncate">
            {message.from[0]?.name || message.from[0]?.address}
          </p>
        </Tooltip>

        <Tooltip text={message.subject || "No subject"} className="max-w-md">
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {message.subject}
          </p>
        </Tooltip>
      </div>

      {/* Action Pill (clickable) */}
      <span
        ref={pillRef}
        className={`relative cursor-pointer text-xs px-2 py-0.5 rounded-full flex-shrink-0 w-20 text-center mx-2 transition-colors duration-150 select-none
          ${
            mode === "applied" && action === "allow"
              ? "bg-green-100 hover:bg-green-300 text-green-700 dark:bg-green-900/30 dark:hover:bg-green-800/40 dark:text-green-300"
              : mode === "applied" &&
                  (action === "filter" ||
                    action === "blocked" ||
                    action === "challenge")
                ? "bg-red-100 hover:bg-red-300 text-red-700 dark:bg-red-900/30 dark:hover:bg-red-800/40 dark:text-red-300"
                : "bg-gray-100 hover:bg-gray-300 text-gray-700 dark:bg-gray-700/30 dark:hover:bg-gray-600/40 dark:text-gray-300"
          }
        `}
        onClick={() => setIsPillMenuOpen((v) => !v)}
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={isPillMenuOpen}
      >
        {mode === "applied" && action === "allow"
          ? "Good"
          : mode === "applied" &&
              (action === "filter" ||
                action === "blocked" ||
                action === "challenge")
            ? "Bad"
            : "No Action"}
        {/* Pill menu popover */}
        {isPillMenuOpen &&
          createPortal(
            <div
              data-pill-menu-popover
              className="fixed z-[99999] min-w-[260px] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-col items-center animate-fade-in"
              style={{
                left: pillRef.current
                  ? pillRef.current.getBoundingClientRect().left +
                    pillRef.current.getBoundingClientRect().width / 2 -
                    130
                  : 0,
                top: pillRef.current
                  ? pillRef.current.getBoundingClientRect().bottom + 8
                  : 0,
              }}
            >
              <div className="text-sm font-semibold mb-2 text-center text-gray-800 dark:text-gray-100">
                {dispositionMsg}
              </div>
              <div className="flex flex-wrap gap-2 justify-center mb-2">
                {buttons.map((btn) => (
                  <button
                    key={btn.label}
                    onClick={() => {
                      btn.onClick();
                      setIsPillMenuOpen(false);
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium shadow-sm border border-transparent focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors ${btn.color}`}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>
            </div>,
            document.body
          )}
      </span>
      {/* menu next to each message, disabling until I have a user for it */}
      {/* <Menu as="div" className="relative flex-shrink-0">
        <Menu.Button className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600">
          <IoMdMore className="h-5 w-5 text-gray-500 dark:text-gray-400" />
        </Menu.Button>
        <Menu.Items className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-700 rounded-md shadow-lg z-10">
          <Menu.Item>
            {({ active }) => (
              <button
                className={`$${
                  active ? "bg-gray-100 dark:bg-gray-600" : ""
                } flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                onClick={() => {
                  if (onViewDetails) {
                    onViewDetails(message.id);
                  }
                }}
              >
                View details
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Menu> */}
    </div>
  );
};

export default MessageRow;
